import { API_URL } from '@/config';

/**
 * Counts what was seen and what was clicked on the fav rows and the directory.
 *
 * Sent with sendBeacon, which is the only way a click on an outbound link gets
 * recorded reliably: a normal request is cancelled the moment the browser
 * starts navigating away, and this is a page whose entire purpose is
 * navigating away.
 *
 * Impressions are deduplicated for the life of the page. A row scrolling in
 * and out of view is the same slot being seen once, and counting it four times
 * would quietly deflate every click-through rate on the page.
 */
const ENDPOINT = `${API_URL}/engagement`;

// Enough to batch a screenful without ever building an unbounded queue.
const FLUSH_AT = 25;
const FLUSH_AFTER_MS = 4000;

let queue = [];
let timer = null;
let listening = false;
const seen = new Set();

function send() {
  clearTimeout(timer);
  timer = null;

  if (!queue.length) return;

  const events = queue;
  queue = [];

  try {
    // text/plain, not application/json: anything else makes this a
    // non-simple cross-origin request needing a CORS preflight, and
    // sendBeacon has no way to perform one - the beacon is queued, never
    // sent, and fails silently. The API decodes the raw body.
    const blob = new Blob([JSON.stringify({ events })], { type: 'text/plain;charset=UTF-8' });
    if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob)) return;

    // sendBeacon refuses over quota, and is missing on older browsers.
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({ events }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    // Analytics must never break the page it is measuring.
  }
}

function listen() {
  if (listening || typeof document === 'undefined') return;
  listening = true;

  // pagehide and hidden are the two that actually fire on mobile; unload does
  // not, and beforeunload is unreliable there.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') send();
  });
  window.addEventListener('pagehide', send);
}

function push(event) {
  listen();
  queue.push(event);

  if (queue.length >= FLUSH_AT) {
    send();
  } else if (!timer) {
    timer = setTimeout(send, FLUSH_AFTER_MS);
  }
}

export default {
  /**
   * Own traffic is excluded here as well as on the server. The row gets
   * clicked constantly while it is being worked on, and at current volumes
   * that would visibly move the numbers.
   */
  enabled(user) {
    return !(user && user.role === 'admin');
  },

  click({
    surface, cellId, lineId, position,
  }) {
    if (!cellId) return;
    push({
      type: 'click', surface, cellId, lineId, position,
    });
    // A click means the page is probably about to go away.
    send();
  },

  impression({
    surface, cellId, lineId, position,
  }) {
    if (!cellId) return;
    const key = `${surface}|${cellId}|${position}`;
    if (seen.has(key)) return;
    seen.add(key);
    push({
      type: 'impression', surface, cellId, lineId, position,
    });
  },

  /** Impressions are per page view, so a route change starts them again. */
  resetPageView() {
    seen.clear();
  },

  flush: send,
};
